import type { CityConfig } from "@/pages/city-template/types";

export const prescottValleyConfig: CityConfig = {
  city: "Prescott Valley",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "prescott-valley",
  route: "/cities/prescott-valley",
  title: "Emergency & Expedited Passport Services Prescott Valley AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Prescott Valley, AZ — as fast as 24 hours. No Prescott Valley passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 220 miles south. A+ BBB rated. Serving Quad Cities, Yavapai County families, and Prescott Valley medical professionals.",
  h1: "Emergency & Expedited Passport Services in Prescott Valley, AZ",
  heroSubtitle: "Prescott Valley sits in Arizona's central highlands about 220 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a Quad Cities medical professional attending international conferences, a Yavapai College student planning study abroad, or a Prescott Valley family with cruise plans.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Prescott%20Valley%20Arizona%20wide%20panoramic%20view%20of%20the%20expanding%20suburban%20community%20with%20the%20Bradshaw%20Mountains%20in%20the%20background%20under%20warm%20golden%20hour%20sunlight%2C%20modern%20residential%20neighborhoods%20spreading%20across%20valley%20floor%2C%20high%20desert%20landscape%20with%20scattered%20juniper%20trees%2C%20clean%20wide%20angle%20composition%2C%20professional%20landscape%20photography%2C%20no%20people%20visible&width=1800&height=700&seq=pv-hero-001&orientation=landscape",
  heroImageAlt: "Prescott Valley Arizona suburban landscape with Bradshaw Mountains at golden hour",
  geoPosition: "34.6100;-112.3157",
  geoPlacename: "Prescott Valley, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 220 miles from Prescott Valley — about 3.5 hours via AZ-69 and I-17",
  },
  nearbyCitiesZips: [
    { name: "Prescott Valley", zips: "86314, 86315" },
    { name: "Prescott", zips: "86301, 86303" },
    { name: "Dewey-Humboldt", zips: "86327" },
    { name: "Chino Valley", zips: "86323" },
    { name: "Cottonwood", zips: "86326" },
    { name: "Camp Verde", zips: "86322" },
    { name: "Mayer", zips: "86333" },
    { name: "Cordes Lakes", zips: "86333" },
    { name: "Bagdad", zips: "86321" },
    { name: "Paulden", zips: "86334" },
    { name: "Black Canyon City", zips: "85324" },
    { name: "Yarnell", zips: "85362" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 220 miles from Prescott Valley" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Quad Cities Hub", desc: "Serving Prescott Valley, Prescott, Chino Valley, and all Yavapai County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Prescott Valley case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Prescott AZ", route: "/cities/prescott" },
    { name: "Expedited passport in Flagstaff AZ", route: "/cities/flagstaff" },
    { name: "Urgent passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Expedited passport in Cottonwood AZ", route: "/cities/cottonwood" },
    { name: "Fast passport in Sedona AZ", route: "/cities/sedona" },
    { name: "Emergency passport in Scottsdale AZ", route: "/cities/scottsdale" },
  ],
  fpcOfficeAddress: "733 N Stone Ave, Tucson, AZ 85701",
  fpcOfficeNote: "Fast Passport Center serves Prescott Valley remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Prescott Valley's location on the AZ-69 corridor makes overnight document shipping fast and reliable from Yavapai County's fastest-growing community.",
  fpcOfficeDistance: "Serving all of Yavapai County from the Quad Cities",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Prescott Valley, Prescott, Dewey-Humboldt, and the Central Arizona highlands. From Yavapai College students to Yavapai Regional Medical Center professionals, we help Quad Cities travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Prescott Valley and nearby communities including Prescott, Dewey-Humboldt, Chino Valley, Cottonwood, Camp Verde, and the entire Quad Cities area.",
  howItWorksIntro: "Most Prescott Valley travelers complete everything online and ship documents overnight. At about 220 miles from Tucson, Fast Passport Center provides fast courier turnaround for Central Arizona residents — ideal for Quad Cities professionals and families planning international trips.",
  mistakesIntro: "Prescott Valley travelers, including medical professionals heading to conferences and Quad Cities families planning vacations, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Prescott Valley and Central Arizona travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Prescott Valley? Here are the most common ones we hear from Quad Cities travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 220 miles to Tucson (3.5-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Yavapai College students studying abroad",
        "Perfect for medical professionals attending conferences",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from PRC or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Prescott Valley is about 220 miles from the Western Passport Center in Tucson. Our couriers make same-day delivery possible, as fast as 24 hours.",
      bullets: ["Central AZ coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Prescott Valley adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving all Quad Cities"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Prescott Valley and Yavapai County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Prescott Valley, Prescott, and Quad Cities residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Yavapai County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Prescott Valley and Central Arizona residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Prescott Valley AZ", description: "Urgent passport processing for Prescott Valley, AZ travelers via Western Passport Center courier — about 220 miles from Prescott Valley." },
    { name: "Passport Renewal Prescott Valley AZ", description: "Fast passport renewal for Prescott Valley and Yavapai County, Arizona residents." },
    { name: "New Passport Prescott Valley AZ", description: "First-time passport application assistance for Prescott Valley and Quad Cities area residents." },
    { name: "Lost Passport Prescott Valley AZ", description: "Passport replacement for lost or stolen passports for Prescott Valley and Central Arizona residents." },
    { name: "Child Passport Prescott Valley AZ", description: "Minor passport services for Prescott Valley and Yavapai County families." },
    { name: "Name Change Passport Prescott Valley AZ", description: "Passport name change service for Prescott Valley, AZ residents." },
  ],
  testimonials: [
    {
      name: "Dr. Angela K.",
      location: "Prescott Valley, AZ",
      rating: 5,
      text: "I'm a cardiologist at Yavapai Regional Medical Center and had a medical conference in Barcelona with 5 days notice. My passport had expired. Fast Passport Center handled everything — new passport in 48 hours. Made my presentation.",
      service: "Emergency Passport",
    },
    {
      name: "Tyler R.",
      location: "Yavapai College Student",
      rating: 5,
      text: "Yavapai College nursing program arranged a clinical rotation in Costa Rica. Realized my passport was expired 3 weeks before departure. Fast Passport Center got me renewed in 6 business days. Incredible service.",
      service: "Expedited Renewal",
    },
    {
      name: "Karen & Bill S.",
      location: "Prescott Valley, AZ",
      rating: 5,
      text: "Quad Cities retirees planning our 40th anniversary cruise to Alaska. We both needed renewals and had only 4 weeks. Fast Passport Center handled both passports and delivered in 7 business days. We made our cruise.",
      service: "Family Passports",
    },
    {
      name: "Marcus D.",
      location: "Dewey-Humboldt, AZ",
      rating: 5,
      text: "Business trip to Tokyo with 10 days notice. Never had a passport. Fast Passport Center walked me through the DS-11 at the Prescott Valley post office, then rushed everything to Tucson. Passport in hand on day 8.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "pv-1",
      name: "Prescott Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8300 E Spouse Dr",
      city: "Prescott Valley",
      state: "AZ",
      zip: "86314",
      phone: "(928) 775-6400",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Prescott Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Prescott Valley residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "pv-2",
      name: "Prescott Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "442 W Goodwin St",
      city: "Prescott",
      state: "AZ",
      zip: "86301",
      phone: "(928) 778-1890",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "8 miles west in downtown Prescott",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternate acceptance facility in nearby Prescott. Located downtown on the historic courthouse plaza.",
      icon: "ri-mail-line",
    },
    {
      id: "pv-3",
      name: "Yavapai County Clerk of Superior Court",
      type: "County Clerk Passport Acceptance",
      address: "120 S Cortez St, Room 107",
      city: "Prescott",
      state: "AZ",
      zip: "86301",
      phone: "(928) 771-3312",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "8 miles west in Prescott Courthouse Plaza",
      website: "https://courts.yavapaiaz.gov/clerk",
      websiteLabel: "Yavapai County Clerk",
      notes: "Accepts passport applications for Yavapai County residents. Convenient downtown Prescott courthouse location.",
      icon: "ri-government-line",
    },
    {
      id: "pv-4",
      name: "Chino Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "460 S Highway 89",
      city: "Chino Valley",
      state: "AZ",
      zip: "86323",
      phone: "(928) 636-2306",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles north of Prescott Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Chino Valley and northern Yavapai County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "pv-5",
      name: "Cottonwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 S Willard St",
      city: "Cottonwood",
      state: "AZ",
      zip: "86326",
      phone: "(928) 634-2281",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles east in Verde Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Verde Valley and eastern Yavapai County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "pv-6",
      name: "Dewey-Humboldt Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12825 E Main St",
      city: "Dewey",
      state: "AZ",
      zip: "86327",
      phone: "(928) 632-5221",
      hours: "Mon–Fri: 8:30 AM – 4:00 PM",
      distance: "8 miles southeast of Prescott Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Dewey-Humboldt and southeastern Quad Cities residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "220 miles — 3.5-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming Prescott Valley Has an Agency — Driving to Tucson Blind",
      description: "Prescott Valley does not have its own passport agency. The closest is the Western Passport Center in Tucson, 220 miles away — and it requires pre-scheduled appointments. Don't waste a 7-hour round trip without confirming an appointment first.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications — Yavapai County Drugstores",
      description: "Passport photos must be exactly 2x2 inches with a white background. While CVS and Walgreens in Prescott Valley generally do a good job, always verify your photos meet exact specifications before leaving the store.",
    },
    {
      icon: "ri-file-list-line",
      title: "Yavapai College Students Using Wrong Form",
      description: "Yavapai College students studying abroad, especially nursing and EMS programs, often confuse DS-11 and DS-82. DS-11 is for first-time passports; DS-82 is for adult renewals. Using the wrong form delays processing.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Quad Cities Families Forgetting Both Parents Rule",
      description: "Minors under 16 need both parents present at the acceptance facility — or one parent with notarized Form DS-3053. Prescott Valley families with one parent working in Phoenix often miss this until it is too late.",
    },
    {
      icon: "ri-clockwise-line",
      title: "Waiting Until Peak Summer Travel Season",
      description: "Prescott Valley's summer is peak passport application season. Standard USPS processing can stretch to 13 weeks during summer months. Start at least 10 weeks before any international travel — especially for Quad Cities families on summer vacation.",
    },
    {
      icon: "ri-flight-takeoff-line",
      title: "Not Checking Passport Validity for Mexico Travel",
      description: "Many Prescott Valley residents drive to Rocky Point (Puerto Peñasco) for weekend getaways. Mexico requires a valid passport — not just a passport card — for air travel. Check your passport book, not just your card.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Prescott Valley, Prescott, or Chino Valley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — about 220 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Prescott Valley or Yavapai County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Prescott Valley from a passport agency?",
      a: "Prescott Valley is approximately 220 miles from the Western Passport Center in Tucson — about a 3.5-hour drive via AZ-69 and I-17. However, the Tucson agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles the courier delivery so you don't need to make the trip.",
    },
    {
      q: "Can Prescott Valley residents use the Prescott post office for passport applications?",
      a: "Yes. Many Prescott Valley residents apply at the Prescott Main Post Office on Goodwin Street, about 8 miles west, or the Prescott Valley Post Office on Spouse Drive. Both are USPS acceptance facilities. For faster processing, combine your acceptance facility visit with Fast Passport Center's expedited courier service.",
    },
    {
      q: "How fast can I get an emergency passport in Prescott Valley?",
      a: "Emergency processing is typically 24–48 hours for Prescott Valley residents. Our couriers deliver directly to the Western Passport Center in Tucson, about 220 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Prescott Valley have its own passport agency?",
      a: "No. The closest regional passport agency is the Western Passport Center in Tucson, about 220 miles from Prescott Valley. Prescott Valley has its own USPS acceptance facility at 8300 E Spouse Drive for first-time applications requiring in-person submission.",
    },
    {
      q: "I'm a Yavapai Regional Medical Center professional — how fast can you process my passport for a medical conference?",
      a: "YRMC and Dignity Health medical professionals are among our frequent clients. Emergency processing delivers passports in 24–48 hours. Standard expedited is 2–9 business days. We keep a dedicated agent on your case so your passport arrives before your conference.",
    },
    {
      q: "Where do Prescott Valley travelers fly internationally?",
      a: "Prescott Valley travelers typically fly internationally through Phoenix Sky Harbor International Airport (PHX, about 95 miles south), or Prescott Regional Airport (PRC, about 10 miles west) for connections to DEN or LAX for international flights.",
    },
    {
      q: "How do I renew my passport in Prescott Valley?",
      a: "Adult renewals (16 or older, issued within 15 years, same name) can be done by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Prescott Valley?",
      a: "CVS Pharmacy at 3040 N Windsong Drive and Walgreens at 7550 E State Route 69 both offer passport photo services in Prescott Valley. Always verify your photos meet the exact 2x2 inch specifications.",
    },
    {
      q: "Do you serve Dewey-Humboldt and Chino Valley residents too?",
      a: "Yes. We serve all of Yavapai County including Dewey-Humboldt, Chino Valley, Mayer, Cordes Lakes, and the entire Quad Cities area. Documents ship overnight from any UPS location in the Prescott area.",
    },
    {
      q: "Can I use a passport card for my cruise from a California port?",
      a: "No. Cruises that depart from U.S. ports and visit foreign countries require a passport book — not a passport card — for re-entry. Prescott Valley cruise travelers should apply for a full passport book well before their sail date.",
    },
    {
      q: "How do Prescott Valley's seasonal population changes affect passport processing?",
      a: "Prescott Valley's population swells during summer months with seasonal residents and visitors escaping Phoenix heat. This increases demand at local acceptance facilities. Book your acceptance clerk appointment early, especially May through September.",
    },
    {
      q: "What documents do I need for a first-time passport in Prescott Valley?",
      a: "You'll need Form DS-11, a certified birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Prescott Valley Post Office on Spouse Drive.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Prescott Valley", address: "3040 N Windsong Dr, Prescott Valley, AZ 86314", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Prescott Valley", address: "7550 E State Rte 69, Prescott Valley, AZ 86314", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Prescott", address: "530 Miller Valley Rd, Prescott, AZ 86301", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Prescott", address: "1000 Willow Creek Rd, Prescott, AZ 86301", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Prescott", address: "1781 E State Rte 69, Prescott, AZ 86301", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "UPS Store — Prescott Valley", address: "7720 E State Rte 69, Prescott Valley, AZ 86314", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Prescott", address: "1105 Commerce Dr, Prescott, AZ 86305", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Yavapai County residents. About 8 miles from Prescott Valley.", featured: true },
    { name: "AZ MVD — Cottonwood", address: "525 S 12th St, Cottonwood, AZ 86326", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Verde Valley and eastern Yavapai County.", featured: false },
    { name: "AZ MVD — Flagstaff", address: "1959 S Woodlands Village Blvd, Flagstaff, AZ 86001", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves northern Arizona including portions of Yavapai County.", featured: false },
  ],
  airports: [
    {
      code: "PRC",
      name: "Prescott Regional Airport",
      distance: "10 miles west of Prescott Valley",
      address: "6500 MacCurdy Dr, Prescott, AZ 86301",
      note: "Prescott's local airport with United Express connections to Denver and Los Angeles. Passport required for international connections through DEN or LAX.",
      flightUrl: "https://www.prcairport.com/",
      flightLabel: "PRC Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "95 miles south of Prescott Valley",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 1.5 hours south via AZ-69 and I-17.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "220 miles south of Prescott Valley",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Southern Arizona's international hub. About 3.5 hours south via I-17 and I-10. Co-located with the Western Passport Center.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
  ],
};